<script setup lang="ts">
import { ref, watch } from 'vue';
import { useElementSize } from '@vueuse/core';

import type { PriceRibbonMonth } from '../priceRibbon';

import PriceCalendarTileView from './components/PriceCalendarTileView.vue';
import PriceCalendarHeader from './components/PriceCalendarHeader.vue';
import type { PriceCalendarState } from './helpers';
import { getPriceCalendarTileRenderSize } from './helpers';

interface PriceCalendarProps {
  selectedMonth: string;
  months: PriceRibbonMonth[];
  priceCalendarState: PriceCalendarState;
  showPrices: boolean;
  allowHoverStates: boolean;
  showHeaderPadding: boolean;
}

const props = defineProps<PriceCalendarProps>();

interface PriceCalendarEmits {
  (eventName: 'select-date', date: Date): void;
  (eventName: 'select-month', month: Date): void;
}

const emit = defineEmits<PriceCalendarEmits>();

const shouldShowMonthsSelector = ref<boolean>(false);
const priceCalendarRef = ref<HTMLDivElement | undefined>(undefined);
const { width: priceCalendarWidth, height: priceCalendarHeight } =
  useElementSize(priceCalendarRef);

function createTileKey(
  calendarRowIndex: number,
  calendarTileIndex: number
): string {
  return `${calendarRowIndex}-${calendarTileIndex}`;
}

function onSelectMonth(month: Date): void {
  emit('select-month', month);
  shouldShowMonthsSelector.value = false;
}

watch([priceCalendarWidth, priceCalendarHeight, props], () => {
  const paddingThreshold = 480;
  const smallHeaderLeftPadding = 12;
  const largeHeaderLeftPadding = 24;
  const headerLeftPadding =
    priceCalendarWidth.value < paddingThreshold
      ? smallHeaderLeftPadding
      : largeHeaderLeftPadding;
  const smallHeaderSize = 65;
  const largeHeaderSize = 73;
  const headerHeight =
    priceCalendarWidth.value < paddingThreshold
      ? smallHeaderSize
      : largeHeaderSize;
  const size = getPriceCalendarTileRenderSize(
    priceCalendarWidth.value,
    priceCalendarHeight.value,
    props.priceCalendarState.getTotalTilesInColumn(),
    {
      headerHeight,
      paddingThreshold,
      smallSidePadding: 8,
      largeSidePadding: 48,
      smallBottomPadding: 8,
      largeBottomPadding: 24,
    }
  );

  if (priceCalendarRef.value) {
    priceCalendarRef.value.style.setProperty(
      '--calendar-header-height',
      `${headerHeight}px`
    );
    if (props.showHeaderPadding) {
      priceCalendarRef.value.style.setProperty(
        '--calendar-header-left-padding',
        `${headerLeftPadding}px`
      );
    }
    priceCalendarRef.value.style.setProperty(
      '--calendar-tile-size',
      `${size}px`
    );
  }
});
</script>

<template>
  <div ref="priceCalendarRef" class="price-calendar">
    <div
      class="header-wrapper"
      :class="{ underline: !shouldShowMonthsSelector }"
    >
      <PriceCalendarHeader
        :should-show-months-selector="shouldShowMonthsSelector"
        :months="months"
        :selected-month-iso-date="props.selectedMonth"
        @select-month="onSelectMonth"
        @toggle-months-selector="
          shouldShowMonthsSelector = !shouldShowMonthsSelector
        "
      >
        <template #headerSecondaryAction>
          <slot name="headerSecondaryAction" />
        </template>
      </PriceCalendarHeader>
    </div>
    <div>
      <div
        v-for="(
          calendarRow, calendarRowIndex
        ) of props.priceCalendarState.getCalendarMatrix()"
        :key="calendarRowIndex"
        class="calendar-row"
      >
        <PriceCalendarTileView
          v-for="(calendarTile, calendarTileIndex) of calendarRow"
          :key="createTileKey(calendarRowIndex, calendarTileIndex)"
          :calendar-tile="calendarTile"
          :show-prices="props.showPrices"
          :allow-hover-states="props.allowHoverStates"
          @select-date="emit('select-date', $event)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.price-calendar {
  // NOTE: Variables:
  // * calendar-weekday-font
  // * calendar-tile-day-font
  // * calendar-tile-day-font
  // * calendar-tile-price-font
  // are set via mixins in /modules/priceCalendar/styles/_price_calendar_mixins.scss

  --calendar-tile-background-color: var(--color-white);
  --calendar-tile-border-radius: var(--border-radius-8);
  --calendar-tile-day-font-color: var(--color-neutral-70);
  --calendar-tile-past-day-font-color: var(--color-neutral-30);
  --calendar-tile-hover-color: var(--color-neutral-10);
  --calendar-tile-info-icon-right: 6px;
  --calendar-tile-info-icon-right-mobile: 0px;
  --calendar-tile-info-icon-top: 1px;
  --calendar-tile-price-font-color: var(--color-neutral-70);
  --calendar-tile-gap: 0px;
  --calendar-tile-day-price-gap: var(--spacing-8);
  --calendar-weekday-font-color: var(--color-neutral-70);
  --calendar-price-placeholder-height: 16px;

  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;

  .header-wrapper {
    width: 100%;
    height: var(--calendar-header-height);
    z-index: 100;
    position: relative;

    &.underline {
      border-bottom: 1px solid var(--color-neutral-30);
    }
  }

  .calendar-row {
    width: 100%;
    display: flex;
    gap: var(--calendar-tile-gap);
  }
}
</style>
